'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import Badge from '@/components/shared/Badge';
import RemovedContent from '@/components/shared/RemovedContent';
import ReportModal from '@/components/shared/ReportModal';
import { mockServices, mockServiceReviews } from '@/data/mockServices';
import { ArrowLeft, Share2, Star } from 'lucide-react';
import ShareModal from '@/components/shared/ShareModal';

function AvatarInitials({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const dim = size === 'lg' ? 'w-14 h-14 text-[16px]' : size === 'md' ? 'w-10 h-10 text-[13px]' : 'w-8 h-8 text-[11px]';
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold`}
      style={{
        background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
        border: '1px solid rgba(76,201,255,0.20)',
        color: '#4CC9FF',
      }}
    >
      {initials}
    </div>
  );
}

function StarRating({ rating, large }: { rating: number; large?: boolean }) {
  const size = large ? 16 : 12;
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg key={star} width={size} height={size} viewBox="0 0 12 12" fill="none">
          <path
            d="M6 1L7.35 4.36L11 4.64L8.5 6.86L9.27 10.5L6 8.5L2.73 10.5L3.5 6.86L1 4.64L4.65 4.36L6 1Z"
            fill={star <= Math.round(rating) ? '#F0A832' : 'rgba(167,163,196,0.20)'}
          />
        </svg>
      ))}
    </div>
  );
}

interface ServiceDetailPageProps {
  params: { serviceSlug: string };
}

const REMOVED_SLUGS = ['removed-service'];

export default function ServiceDetailPage({ params }: { params: { serviceSlug: string } }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  if (REMOVED_SLUGS.includes(params.serviceSlug)) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="service" backHref="/services" backLabel="Back to Services" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const service = mockServices.find((s) => s.slug === params.serviceSlug);

  if (!service) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
              <Link href="/services" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Services
              </Link>
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="service" title="Service Not Found" description="This service listing doesn't exist or may have been removed." backHref="/services" backLabel="Back to Services" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const reviews = mockServiceReviews.filter((r) => r.serviceSlug === service.slug);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {/* Back nav */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
            <Link href="/services" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Services
            </Link>
          </div>
        </div>

        <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main */}
            <div className="lg:col-span-2 space-y-6">
              {/* Service header */}
              <div className="neon-card p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="text-[11px] px-2.5 py-1 rounded font-medium"
                    style={{ background: 'rgba(76,201,255,0.08)', color: '#4CC9FF', border: '1px solid rgba(76,201,255,0.15)' }}
                  >
                    {service.category}
                  </span>
                  {service.isSponsor && <Badge variant="sponsor" />}
                </div>
                <h1 className="font-display font-bold text-[26px] leading-tight mb-4" style={{ color: '#F2F4FF' }}>
                  {service.title}
                </h1>

                {/* Provider */}
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[16px]"
                    style={{ background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))', border: '1px solid rgba(76,201,255,0.20)', color: '#4CC9FF' }}
                  >
                    {service.providerName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <Link href={`/directory/${service.providerSlug}`} className="font-semibold text-[15px] transition-colors duration-150 hover:text-[#4CC9FF]" style={{ color: '#F2F4FF' }}>
                      {service.providerName}
                    </Link>
                    <p className="text-[13px]" style={{ color: '#A7A3C4' }}>
                      {service.isSponsor ? 'Sponsor' : `${service.providerRole} · ${service.providerTower} · Unit ${service.providerUnit}`}
                    </p>
                  </div>
                </div>

                {/* Rating summary */}
                <div
                  className="flex items-center gap-4 px-4 py-3 rounded-lg mb-5"
                  style={{ background: 'rgba(240,168,50,0.06)', border: '1px solid rgba(240,168,50,0.15)' }}
                >
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} width="16" height="16" viewBox="0 0 12 12" fill="none">
                        <path d="M6 1L7.35 4.36L11 4.64L8.5 6.86L9.27 10.5L6 8.5L2.73 10.5L3.5 6.86L1 4.64L4.65 4.36L6 1Z" fill={star <= Math.round(service.rating) ? '#F0A832' : 'rgba(167,163,196,0.20)'} />
                      </svg>
                    ))}
                  </div>
                  <div>
                    <span className="font-display font-bold text-[20px] tabular-nums" style={{ color: '#F0A832' }}>
                      {service.rating.toFixed(1)}
                    </span>
                    <span className="text-[13px] ml-2" style={{ color: '#A7A3C4' }}>
                      from {service.reviewCount} review{service.reviewCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <p className="text-[14.5px] leading-relaxed mb-5" style={{ color: 'rgba(242,244,255,0.80)' }}>
                  {service.description}
                </p>

                {service.rate && (
                  <div className="px-4 py-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <p className="text-[12px] font-medium uppercase tracking-wider mb-1" style={{ color: 'rgba(167,163,196,0.55)' }}>Pricing</p>
                    <p className="text-[14px] font-medium" style={{ color: '#F2F4FF' }}>{service.rate}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-5">
                  {service.tags.map((tag: string) => (
                    <span key={tag} className="text-[12px] px-2.5 py-1 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Reviews */}
              <div>
                <h2 className="font-display font-semibold text-[18px] mb-4" style={{ color: '#F2F4FF' }}>
                  Reviews <span className="text-[14px] font-normal" style={{ color: '#A7A3C4' }}>({reviews.length})</span>
                </h2>

                {reviews.length === 0 ? (
                  <div className="neon-card p-8 text-center">
                    <Star size={24} className="mx-auto mb-3" style={{ color: 'rgba(167,163,196,0.30)' }} />
                    <p className="text-[14px]" style={{ color: '#A7A3C4' }}>No reviews yet. Be the first to leave one.</p>
                    <p className="text-[12.5px] mt-2" style={{ color: 'rgba(167,163,196,0.45)' }}>Reviews are from verified residents only. No anonymous reviews.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review: typeof mockServiceReviews[0]) => (
                      <div key={review.id} className="neon-card p-5">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[11px]" style={{ background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))', border: '1px solid rgba(76,201,255,0.20)', color: '#4CC9FF' }}>
                            {review.reviewerName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <span className="font-semibold text-[14px]" style={{ color: '#F2F4FF' }}>{review.reviewerName}</span>
                                <span className="text-[12px] ml-2" style={{ color: '#A7A3C4' }}>{review.reviewerRole} · {review.reviewerTower}</span>
                              </div>
                              <span className="text-[11.5px]" style={{ color: 'rgba(167,163,196,0.45)' }}>{review.date}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex items-center gap-0.5">
                                {[1,2,3,4,5].map((s) => <svg key={s} width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1L7.35 4.36L11 4.64L8.5 6.86L9.27 10.5L6 8.5L2.73 10.5L3.5 6.86L1 4.64L4.65 4.36L6 1Z" fill={s <= Math.round(review.rating) ? '#F0A832' : 'rgba(167,163,196,0.20)'} /></svg>)}
                              </div>
                              <span className="text-[11px] px-2 py-0.5 rounded" style={{ background: 'rgba(122,92,255,0.08)', color: '#9B7FFF', border: '1px solid rgba(122,92,255,0.15)' }}>{review.serviceProvided}</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-[13.5px] leading-relaxed" style={{ color: 'rgba(242,244,255,0.75)' }}>{review.body}</p>
                      </div>
                    ))}
                    <p className="text-[12px] text-center" style={{ color: 'rgba(167,163,196,0.40)' }}>Reviews are from verified residents only. Providers do not reply to reviews.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <div className="neon-card p-5">
                <h3 className="font-display font-semibold text-[14px] mb-3" style={{ color: '#F2F4FF' }}>Interested?</h3>
                <p className="text-[13px] mb-4" style={{ color: '#A7A3C4' }}>Contact {service.providerName} through the Conversations section or their profile.</p>
                <Link href={`/directory/${service.providerSlug}`} className="btn-primary w-full text-center text-[13px] py-2.5 block">View Profile</Link>
              </div>

              <div className="neon-card p-5 space-y-2">
                <button
                  onClick={() => setShareOpen(true)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 hover:bg-white/[0.06] active:scale-[0.98]"
                  style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <Share2 size={14} /> Share listing
                </button>
                <button
                  onClick={() => setReportOpen(true)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 hover:bg-white/[0.06] hover:text-[#F2F4FF] active:scale-[0.98]"
                  style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  Report this listing
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Back CTA */}
        <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Link href="/services" className="btn-secondary text-[14px] px-5 py-2.5 inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to Services
          </Link>
        </div>

        <ReportModal isOpen={reportOpen} onClose={() => setReportOpen(false)} contentType="service" contentLabel={service.title} />
        <ShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} contentType="service" title={service.title} />
      </main>
      <SiteFooter />
    </div>
  );
}
